// src/tabs/logements/LivretModulePhotoCover.jsx — Sprint A.9.1 PR-A91-2 (ADR-128)
//
// Composant upload photo couverture module livret. 1 photo unique par module.
// Pattern signed URL upload réutilisé de LivretGalleryEditor :
//   1. POST livret_photo_upload_signed_url { logement_id, filename, scope:'module', module_id }
//   2. PUT signed_url avec File compressé (direct Storage)
//   3. POST livret_module_photo_upsert { module_id, logement_id, storage_path, public_url }
//
// Mitigations pessimist DEEP :
//   - F-MOY-2 (anti-pattern F2 useState snapshot one-shot) : re-sync currentPhoto
//     via useEffect([module_id]) pour réinitialiser à chaque ouverture sur
//     module différent (sinon snapshot mount écrase props new module).
//
// Affichage Option B architect : uniquement en mode édition (module.id existant).

import { useState, useEffect, useCallback } from "react";
import { DS } from "../../lib/ui";
import { Image as ImageIcon, ImagePlus, RotateCcw, Trash2, Map, AlertTriangle } from "lucide-react";
import { supabase } from "../../supabase";

// Helper auth headers admin (Bearer JWT)
async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

// Compression image côté client (browser-image-compression lazy-load)
// Fallback file original si échec (Safari iOS 13 ou autre).
async function maybeCompress(file) {
  try {
    const { default: imageCompression } = await import(
      "browser-image-compression"
    );
    return await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
    });
  } catch (_) {
    return file;
  }
}

export default function LivretModulePhotoCover({
  module_id,
  logement_id,
  currentPhoto: initialPhoto,
  onPhotoChange,
  disabled,
}) {
  // F-MOY-2 mitigation : NE PAS faire useState(initialPhoto) snapshot mount.
  // Re-sync à chaque changement de module_id (réouverture modal sur autre module).
  const [currentPhoto, setCurrentPhoto] = useState(initialPhoto ?? null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updatingFitMode, setUpdatingFitMode] = useState(false);
  const [error, setError] = useState(null);

  // F-MOY-2 : re-sync sur changement module_id (anti-pattern F2 corrigé)
  useEffect(() => {
    setCurrentPhoto(initialPhoto ?? null);
    setError(null);
  }, [module_id, initialPhoto]);

  const busy = uploading || deleting || updatingFitMode || disabled;

  const handleUpload = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      e.target.value = ""; // reset input pour re-upload same file possible
      if (!file) return;
      if (!module_id || !logement_id) {
        setError("module_id ou logement_id manquant");
        return;
      }
      setUploading(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const compressed = await maybeCompress(file);

        // 1. Signed URL scope='module'
        const sigResp = await fetch(
          "/api/beds24-mapping?action=livret_photo_upload_signed_url",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              action: "livret_photo_upload_signed_url",
              logement_id,
              filename: file.name,
              scope: "module",
              module_id,
            }),
          },
        );
        const sigJson = await sigResp.json();
        if (!sigResp.ok) {
          throw new Error(sigJson.message || sigJson.error || "signed_url_failed");
        }

        // 2. PUT direct Storage (signed URL)
        const putResp = await fetch(sigJson.signed_url, {
          method: "PUT",
          headers: { "Content-Type": compressed.type || "image/jpeg" },
          body: compressed,
        });
        if (!putResp.ok) {
          throw new Error(`Upload Storage échoué (HTTP ${putResp.status})`);
        }

        // 3. UPSERT row livret_module_photos
        const upResp = await fetch(
          "/api/beds24-mapping?action=livret_module_photo_upsert",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              action: "livret_module_photo_upsert",
              module_id,
              logement_id,
              storage_path: sigJson.storage_path,
              public_url: sigJson.public_url,
              order_index: 0,
            }),
          },
        );
        const upJson = await upResp.json();
        if (!upResp.ok) {
          // Rollback Storage best-effort (path uploaded mais SQL KO)
          try {
            await fetch("/api/beds24-mapping?action=livret_photo_delete", {
              method: "POST",
              headers,
              body: JSON.stringify({
                action: "livret_photo_delete",
                storage_path: sigJson.storage_path,
              }),
            });
          } catch (_) {
            /* swallow */
          }
          throw new Error(upJson.message || upJson.error || "upsert_failed");
        }

        // Success : update state local + notify parent
        const newPhoto = upJson.photo;
        setCurrentPhoto(newPhoto);
        onPhotoChange?.(newPhoto);
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setUploading(false);
      }
    },
    [module_id, logement_id, onPhotoChange],
  );

  // ADR-168 / M-3 pessimist — toggle fit_mode post-upload (sans avoir à
  // ré-uploader la photo). Réutilise livret_module_photo_upsert UPDATE branch.
  const handleFitModeChange = useCallback(
    async (newFitMode) => {
      if (!currentPhoto || updatingFitMode) return;
      if (newFitMode === currentPhoto.fit_mode) return;
      setUpdatingFitMode(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(
          "/api/beds24-mapping?action=livret_module_photo_upsert",
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              action: "livret_module_photo_upsert",
              module_id,
              logement_id,
              storage_path: currentPhoto.storage_path,
              public_url: currentPhoto.public_url,
              alt_text: currentPhoto.alt_text || {},
              order_index: currentPhoto.order_index ?? 0,
              fit_mode: newFitMode,
            }),
          },
        );
        const json = await resp.json();
        if (!resp.ok) {
          throw new Error(json.message || json.error || "fit_mode_update_failed");
        }
        const updated = json.photo;
        setCurrentPhoto(updated);
        onPhotoChange?.(updated);
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setUpdatingFitMode(false);
      }
    },
    [currentPhoto, module_id, logement_id, onPhotoChange, updatingFitMode],
  );

  const handleDelete = useCallback(async () => {
    if (!currentPhoto?.id) return;
    if (
      !window.confirm(
        "Supprimer la photo de couverture ?\nCette action est irréversible.",
      )
    )
      return;
    setDeleting(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        "/api/beds24-mapping?action=livret_module_photo_delete",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            action: "livret_module_photo_delete",
            photo_id: currentPhoto.id,
            module_id,
            logement_id,
          }),
        },
      );
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.message || json.error || "delete_failed");
      }
      setCurrentPhoto(null);
      onPhotoChange?.(null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setDeleting(false);
    }
  }, [currentPhoto, module_id, logement_id, onPhotoChange]);

  // Styles DS-consistent
  const containerStyle = {
    border: `0.5px solid ${DS.toolBorder}`,
    borderRadius: 8,
    padding: 12,
    background: DS.toolBg,
    marginBottom: 14,
  };

  const labelHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: DS.textSecondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 8,
  };

  const btnUploadStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    background: busy ? "#cbd5e1" : DS.corail,
    color: "#fff",
    borderRadius: DS.radiusControl,
    cursor: busy ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: DS.fontBody,
  };

  const btnSecondaryStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: DS.card,
    color: DS.textSecondary,
    border: `0.5px solid ${DS.borderHover}`,
    borderRadius: DS.radiusControl,
    cursor: busy ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: DS.fontBody,
  };

  const btnDangerStyle = {
    ...btnSecondaryStyle,
    color: DS.danger,
    borderColor: DS.border,
  };

  return (
    <div style={containerStyle}>
      <div style={labelHeaderStyle}><ImageIcon size={13} strokeWidth={1.8} /> Photo de couverture (optionnel)</div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 8,
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 6,
            color: DS.dangerText,
            fontSize: 11,
            marginBottom: 10,
          }}
        >
          <AlertTriangle size={14} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
        </div>
      )}

      {currentPhoto ? (
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <img
            src={`${currentPhoto.public_url}?width=300&quality=70&format=webp`}
            alt=""
            style={{
              width: 140,
              height: 90,
              objectFit: currentPhoto.fit_mode === "contain" ? "contain" : "cover",
              borderRadius: 8,
              border: `1px solid ${DS.border}`,
              background:
                currentPhoto.fit_mode === "contain"
                  ? `linear-gradient(135deg, ${DS.skyDeep} 0%, ${DS.navy} 100%)`
                  : "#f0f4f9",
              flexShrink: 0,
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flex: 1,
            }}
          >
            <div style={{ fontSize: 11, color: "#888" }}>
              {uploading ? "Remplacement en cours…" : "Photo actuelle"}
            </div>

            {/* ADR-168 — Toggle fit_mode (cover/contain) post-upload */}
            <FitModeToggle
              value={currentPhoto.fit_mode || "cover"}
              onChange={handleFitModeChange}
              disabled={busy}
              loading={updatingFitMode}
            />

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <label style={btnUploadStyle}>
                {uploading ? "Envoi…" : <><RotateCcw size={13} strokeWidth={1.8} /> Remplacer</>}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleUpload}
                  disabled={busy}
                  style={{ display: "none" }}
                />
              </label>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="fbz-press"
                style={btnDangerStyle}
              >
                {deleting ? "Suppression…" : <><Trash2 size={13} strokeWidth={1.8} /> Supprimer</>}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <label style={btnUploadStyle}>
            {uploading ? "Envoi…" : <><ImagePlus size={14} strokeWidth={1.8} /> Ajouter une photo</>}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleUpload}
              disabled={busy}
              style={{ display: "none" }}
            />
          </label>
          <div
            style={{
              fontSize: 11,
              color: DS.textMuted,
              marginTop: 6,
              fontStyle: "italic",
            }}
          >
            JPEG, PNG ou WebP. Compression automatique max 1200px.
          </div>
        </div>
      )}
    </div>
  );
}

// ADR-168 — Toggle "📸 Photo (cadrée)" / "🗺️ Plan / infographie (image entière)"
// 2 boutons aria-pressed minHeight 44 WCAG 2.5.5. Cover par défaut (cadré).
function FitModeToggle({ value, onChange, disabled, loading }) {
  const containerStyle = {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    padding: "6px 0",
  };
  const btnBase = {
    padding: "8px 12px",
    minHeight: 44,
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: DS.fontBody,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background 0.15s, border-color 0.15s",
  };
  const btn = (active) => ({
    ...btnBase,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: active ? DS.marine : DS.card,
    color: active ? "#fff" : DS.marine,
    border: `0.5px solid ${active ? DS.marine : DS.borderHover}`,
  });
  return (
    <div style={containerStyle}>
      <button
        type="button"
        aria-pressed={value === "cover"}
        onClick={() => onChange("cover")}
        disabled={disabled}
        style={btn(value === "cover")}
        title="Photo réelle — l'image est cadrée au format de la carte (peut rogner les bords)"
      >
        <ImageIcon size={13} strokeWidth={1.8} /> Photo (cadrée)
      </button>
      <button
        type="button"
        aria-pressed={value === "contain"}
        onClick={() => onChange("contain")}
        disabled={disabled}
        style={btn(value === "contain")}
        title="Plan, infographie ou schéma — affichage de l'image entière (peut laisser des bandes)"
      >
        <Map size={13} strokeWidth={1.8} /> Plan / infographie (image entière)
      </button>
      {loading && (
        <span style={{ fontSize: 11, color: DS.textMuted, alignSelf: "center" }}>
          Mise à jour…
        </span>
      )}
    </div>
  );
}
